import { useState, useEffect } from 'react'
import useSWR, { SWRResponse } from 'swr'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { useParams } from 'react-router-dom'
import { Container, Typography, Box, Paper, Grid, Button, TextField } from '@mui/material'
import { IconUser } from '@tabler/icons-react'
import Loading from '../../components/Loading'
import axiosInstance from '../../utils/axiosInstance'
import { fixImgSrc } from '../../utils/tools'
import './style.scss'

type TimageItem = {
  image: { url: string }
  name: string
}

interface Vacancy {
  id: string
  jobName: string
  place: string
  createdAt: string
  channel: string[]
  runtime: number
  jobAdLink: string
  adDescription: string
  headerImage: TimageItem | null
  customer: {
    id: string
    name: string
    logoUrl?: string
  }
}

interface VacancyPageProps {
  isPreview?: boolean
  orderId?: string
}

const VacancyPage = ({ isPreview = false, orderId }: VacancyPageProps) => {
  const { id: urlId } = useParams<{ id: string }>()
  // Use orderId if provided (preview mode), otherwise use the URL parameter
  const id = orderId || urlId

  const {
    data: response,
    error,
    isLoading,
  }: SWRResponse = useSWR(id ? `/vacancies/${id}` : null, axiosInstance)

  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const vacancy: Vacancy | null = response?.data || null

  // Set document title when vacancy data is loaded
  useEffect(() => {
    // Only set document title when not in preview mode
    if (!isPreview && vacancy?.jobName) {
      document.title = `${vacancy.jobName} | Vacancy`
    } else if (!isPreview) {
      document.title = 'Vacancy Details'
    }

    return () => {
      if (!isPreview) {
        document.title = 'Recruspin'
      }
    }
  }, [vacancy, isPreview])

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')

    try {
      // Format the field data as required
      const fieldData = [
        {
          name: 'full_name',
          values: [`${formData.firstName} ${formData.lastName}`],
        },
        {
          name: 'phone_number',
          values: [formData.phone],
        },
        {
          name: 'email',
          values: [formData.email],
        },
      ]

      // Submit the application to create a lead
      const response = await axiosInstance.post('/lead/vacancy', {
        fieldData,
        platform: 'internalRecruspin',
        orderId: vacancy?.id, // Connect with the related order
      })

      if (response.status === 201) {
        // Reset form and show success
        setFormData({ firstName: '', lastName: '', email: '', phone: '' })
        setShowForm(false)
        setSubmitSuccess(true)
      } else {
        throw new Error('Server returned an error response')
      }
    } catch (error) {
      console.error('Error submitting application:', error)
      setSubmitError(
        'Es gab ein Problem beim Absenden Ihrer Bewerbung. Bitte versuchen Sie es später erneut.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleApplyClick = () => {
    setShowForm(true)
    // Scroll to form
    setTimeout(() => {
      const formElement = document.getElementById('application-form')
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth' })
      }
    }, 100)
  }

  const headerImage = vacancy?.headerImage

  if (isLoading) {
    return <Loading />
  }

  if (error || !vacancy) {
    return (
      <Container maxWidth="md" className="vacancy-error">
        <Typography variant="h4" component="h1" gutterBottom>
          {error?.message || 'Vacancy not found'}
        </Typography>
      </Container>
    )
  }

  return (
    <div className="vacancy-dark-bg">
      <Container
        maxWidth="md"
        className="vacancy-container vacancy-centered"
        sx={{
          mt: isPreview ? 0 : 4,
          mb: isPreview ? 0 : 4,
          width: '100%',
          margin: isPreview ? '0 auto' : undefined,
        }}
      >
        {/* Customer Logo */}
        <div className="vacancy-logo">
          {vacancy.customer.logoUrl ? (
            <img src={fixImgSrc(vacancy.customer.logoUrl)} alt={`${vacancy.customer.name} logo`} />
          ) : (
            <IconUser width="60px" height="60px" />
          )}
        </div>
        {/* Job Title and CTA */}
        <Typography
          variant="h3"
          component="h1"
          align="center"
          className="vacancy-green-title"
          mt={3}
          mb={2}
        >
          {vacancy.jobName}
        </Typography>

        {/* Only show application button when not in preview mode */}
        {!isPreview && (
          <Box display="flex" justifyContent="center" mt={2} mb={3}>
            <Button variant="contained" className="vacancy-cta-btn" onClick={handleApplyClick}>
              Jetzt bewerben
            </Button>
          </Box>
        )}

        {/* Display header image if available */}
        {headerImage && (
          <div className="vacancy-preview-image">
            <img
              src={fixImgSrc(headerImage.image.url)}
              alt={headerImage.name || 'Header image'}
              className="preview-image"
            />
          </div>
        )}

        {/* Location and Work Time */}
        <Box display="flex" justifyContent="center" gap={4} mt={3} mb={3}>
          <Box display="flex" alignItems="center" gap={1}>
            <LocationOnIcon color="success" fontSize="small" />
            <Typography variant="body1">{vacancy.place}</Typography>
          </Box>
        </Box>
        {/* Description */}
        <Box className="vacancy-description" mb={3}>
          <div
            className="job-description-content"
            dangerouslySetInnerHTML={{ __html: vacancy.adDescription }}
          ></div>
        </Box>
        {/* Privacy Notice with CTA Button */}
        {!isPreview && (
          <Paper elevation={2} className="vacancy-privacy-notice">
            <Box display="flex" justifyContent="center" mb={3}>
              <Button variant="contained" className="vacancy-cta-btn" onClick={handleApplyClick}>
                Jetzt bewerben
              </Button>
            </Box>
            <Typography variant="body2" align="center" color="textSecondary">
              Alle Angaben werden vertraulich behandelt und nur dafür verwendet, um uns persönlich
              mit dir bezüglich einer Anstellung in Verbindung zu setzen. Du verpflichtest Dich zu
              nichts.
            </Typography>
          </Paper>
        )}

        {/* Application Form */}
        {showForm && !isPreview && (
          <Paper elevation={3} className="vacancy-application-form" id="application-form">
            <Typography variant="h5" align="center" className="vacancy-green-title" gutterBottom>
              Bewerbungsformular
            </Typography>
            {submitError && (
              <Typography color="error" align="center" gutterBottom>
                {submitError}
              </Typography>
            )}
            <form onSubmit={handleFormSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Vorname"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    placeholder="Vorname"
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nachname"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    placeholder="Nachname"
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="E-Mail"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    placeholder="hello@recruspin"
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Telefon"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    placeholder="0173 01234567"
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="center" mt={2}>
                  <Button
                    variant="contained"
                    className="vacancy-cta-btn"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Wird gesendet...' : 'Jetzt abschicken'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        )}

        {/* Success message after submission */}
        {submitSuccess && !showForm && !isPreview && (
          <Paper elevation={3} className="vacancy-application-success" id="application-success">
            <Typography variant="h5" align="center" className="vacancy-green-title" gutterBottom>
              Vielen Dank für Ihre Bewerbung!
            </Typography>
            <Typography align="center" paragraph>
              Wir haben Ihre Bewerbung erhalten und werden uns in Kürze mit Ihnen in Verbindung
              setzen.
            </Typography>
          </Paper>
        )}
      </Container>
    </div>
  )
}

export default VacancyPage
